import type { RoutePathOption, RoutePlan, RouteSegmentPlan } from '@gpx-spa/geo-domain'
import {
  jaccardBufferedPathPairs,
  LEARNED_GOOGLE_JACCARD_DUPLICATE_THRESHOLD,
  type JaccardBufferedPathPairInput,
} from '@gpx-spa/geo-adapters'
import { LEARNED_MAX_ALTERNATIVES_PER_SEGMENT } from './learned-route-offers'
import { googleFirstThenLearned } from './merge-route-plans-by-segment'

function pathCoords(path: RoutePathOption): [number, number][] | null {
  const c = path.coordinates
  if (!c || c.length < 2) return null
  return c
}

function segmentKey(segment: RouteSegmentPlan): string {
  return `${segment.fromStopIndex}:${segment.toStopIndex}`
}

export type LearnedGoogleMatchEvaluation = {
  duplicateIds: Set<string>
  maxMatch: number | null
}

export async function evaluateLearnedMatchesAgainstGoogle(
  google: RoutePlan | null | undefined,
  learned: RoutePlan | null | undefined,
  corridorBufferM: number,
  jaccardThreshold = LEARNED_GOOGLE_JACCARD_DUPLICATE_THRESHOLD,
): Promise<LearnedGoogleMatchEvaluation> {
  if (!learned) return { duplicateIds: new Set(), maxMatch: null }
  const duplicateThreshold = Math.min(1, Math.max(0, jaccardThreshold))
  const googleByKey = new Map<string, RouteSegmentPlan>()
  for (const segment of google?.segments ?? []) {
    googleByKey.set(segmentKey(segment), segment)
  }

  const pairs: JaccardBufferedPathPairInput[] = []
  for (const learnedSegment of learned.segments) {
    const googleSegment = googleByKey.get(segmentKey(learnedSegment))
    const googlePaths = (googleSegment?.paths ?? []).filter((p) => pathCoords(p))
    if (!googlePaths.length) continue
    for (const learnedPath of learnedSegment.paths) {
      const learnedCoords = pathCoords(learnedPath)
      if (!learnedCoords || !learnedPath.id) continue
      for (const googlePath of googlePaths) {
        const googleCoords = pathCoords(googlePath)
        if (!googleCoords) continue
        pairs.push({
          learnedPathId: `${segmentKey(learnedSegment)}:${learnedPath.id}`,
          googlePathId: googlePath.id ?? 'google',
          learned: learnedCoords,
          google: googleCoords,
          radiusMeters: corridorBufferM,
        })
      }
    }
  }

  let duplicateIds = new Set<string>()
  let maxMatch: number | null = null
  try {
    const scores = await jaccardBufferedPathPairs(pairs)
    for (const score of scores) {
      if (score.jaccard != null) {
        maxMatch =
          maxMatch == null ? score.jaccard : Math.max(maxMatch, score.jaccard)
      }
      if (
        score.jaccard != null &&
        score.jaccard >= duplicateThreshold
      ) {
        duplicateIds.add(score.learnedPathId)
      }
    }
  } catch {
    duplicateIds = new Set()
    maxMatch = null
  }

  return { duplicateIds, maxMatch }
}

export async function filterLearnedDuplicatesAgainstGoogle(
  google: RoutePlan | null | undefined,
  learned: RoutePlan | null | undefined,
  corridorBufferM: number,
  jaccardThreshold = LEARNED_GOOGLE_JACCARD_DUPLICATE_THRESHOLD,
  onEvaluated?: (evaluation: LearnedGoogleMatchEvaluation) => void,
): Promise<RoutePlan | null> {
  if (!learned) {
    onEvaluated?.({ duplicateIds: new Set(), maxMatch: null })
    return null
  }
  const evaluation = await evaluateLearnedMatchesAgainstGoogle(
    google,
    learned,
    corridorBufferM,
    jaccardThreshold,
  )
  onEvaluated?.(evaluation)

  return {
    segments: learned.segments.map((segment) => {
      const kept = segment.paths.filter((path) => {
        if (!path.id) return true
        return !evaluation.duplicateIds.has(
          `${segmentKey(segment)}:${path.id}`,
        )
      })
      return {
        ...segment,
        paths: kept.slice(0, LEARNED_MAX_ALTERNATIVES_PER_SEGMENT),
      }
    }),
    meta: learned.meta,
  }
}

export function orderMergedSegmentPaths(plan: RoutePlan): RoutePlan {
  return {
    ...plan,
    segments: plan.segments.map((segment) => ({
      ...segment,
      paths: googleFirstThenLearned(segment.paths),
    })),
  }
}
